<script setup lang="ts">
import { computed, watch } from 'vue'

import { useReportInsightsPanel } from '../../composables/useReportInsightsPanel'
import type { InsightResult } from '../../models/insight.types'
import { useInsightStore } from '../../stores/insightStore'

import InsightCard from './InsightCard.vue'

import { useI18n } from '@/shared/i18n'
import { useFormat } from '@/shared/lib/format'
import { Alert } from '@/shared/ui/alert'
import { Button } from '@/shared/ui/button'
import { Drawer } from '@/shared/ui/drawer'
import { Icon } from '@/shared/ui/icon'
import { Skeleton } from '@/shared/ui/skeleton'

const emit = defineEmits<{
  open: [insight: InsightResult]
  'open-all': []
}>()
const { t } = useI18n()
const format = useFormat()
const store = useInsightStore()
const panel = useReportInsightsPanel({ limit: 20 })
const isVisible = computed(
  () => panel.state.value === 'loading' || panel.state.value === 'error' || panel.count.value > 0,
)

watch(isVisible, (visible) => {
  if (!visible) store.closePanel()
})

function openInsight(insight: InsightResult): void {
  store.closePanel()
  emit('open', insight)
}

function openAll(): void {
  store.closePanel()
  emit('open-all')
}
</script>

<template>
  <div
    v-if="isVisible"
    class="report-insights-panel inline-flex max-[76rem]:hidden"
    data-testid="report-insights-panel"
  >
    <button
      type="button"
      class="app-topbar__insights inline-flex h-[var(--ui-shell-topbar-insights-height)] cursor-pointer items-center rounded-[var(--ui-shell-topbar-control-radius)] border-[0.5px] border-[color:var(--ui-shell-topbar-insights-accent)] bg-[color:var(--ui-shell-topbar-insights-bg)] px-[var(--ui-shell-topbar-insights-padding-inline)] font-sans text-xs font-bold leading-4 text-[color:var(--ui-shell-topbar-insights-accent)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--ui-color-ring)]"
      :aria-label="t('insights.panel.open')"
      :aria-expanded="store.panelOpen"
      @click="store.togglePanel()"
    >
      <Icon
        name="message-dot"
        :size="12"
        class="report-insights-panel__icon fill-current stroke-current"
        aria-hidden="true"
      />
      <span
        v-if="panel.count.value > 0"
        class="report-insights-panel__count h-auto min-w-0 rounded-none bg-transparent p-0 font-[inherit] text-inherit"
      >
        {{ format.formatNumber(panel.count.value) }}
      </span>
      <span class="report-insights-panel__title">{{ t('insights.panel.title') }}</span>
    </button>

    <Drawer
      v-model="store.panelOpen"
      placement="right"
      width="35rem"
      max-width="35rem"
      :close-label="t('ui.close')"
    >
      <template #header>
        <div class="flex min-w-0 items-center justify-between gap-md">
          <div class="min-w-0">
            <div class="flex items-center gap-sm">
              <span class="truncate">{{ t('insights.panel.title') }}</span>
              <span
                class="relative grid min-h-6 min-w-6 place-items-center rounded-full bg-accent px-xs text-[0.6875rem] font-semibold text-primary-fg"
              >
                {{ format.formatNumber(panel.count.value) }}
              </span>
            </div>
            <p class="m-0 mt-2xs text-sm font-normal leading-5 text-muted">
              {{ t('insights.panel.subtitle') }}
            </p>
          </div>
          <Button variant="ghost" size="xs" @click="openAll">
            {{ t('insights.teaser.seeAll') }}
          </Button>
        </div>
      </template>

      <div v-if="panel.state.value === 'loading'" class="report-insights-panel__list grid gap-sm">
        <Skeleton v-for="item in 5" :key="item" height="6rem" />
      </div>
      <Alert
        v-else-if="panel.state.value === 'error'"
        tone="danger"
        :title="t('insights.error.load')"
      >
        <Button variant="outline" size="xs" @click="panel.reload">
          {{ t('insights.error.retry') }}
        </Button>
      </Alert>
      <div v-else class="report-insights-panel__list grid gap-sm">
        <InsightCard
          v-for="insight in panel.insights.value"
          :key="insight.id"
          :insight="insight"
          compact
          @open="openInsight"
        />
      </div>
    </Drawer>
  </div>
</template>

<style scoped>
.report-insights-panel__icon :deep(path:last-child) {
  stroke: var(--ui-color-surface);
  stroke-width: 3;
}
</style>
